/**
 * One answer to *"will asking again go differently?"*, for both offline queues.
 *
 * Photos and mutations fail in the same world and must not meet a tech with two different
 * rhythms — so the rule lives here rather than beside either of them. It was written for
 * `UploadQueue`'s drain first (§4 Wave 1) and moved out whole when the mutation log needed
 * exactly the same distinction; a second copy would have drifted the first time a status
 * changed sides.
 */

/**
 * Whether a status means *this write* will never be accepted, however many times it is sent.
 *
 * Transient stops a drain, because there is no point burning a hundred requests down a dead
 * connection. Permanent marks the one item and moves on, because one refusal must not hold
 * up the fifty behind it — and because a "Try again" over a permanent refusal is a button
 * that can only fail, which is the same defect `ForbiddenState` exists to prevent one layer
 * up.
 *
 * 401 is deliberately transient: a session that timed out in a basement is fixed by signing
 * in, and throwing the work away over it would be absurd. So are 408 and 429, which are the
 * server asking to be asked later.
 */
export function isPermanentRefusal(status: number): boolean {
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * The HTTP status inside a tRPC client error, or `0` when there is none.
 *
 * `0` is not a fallback that guesses — it is what "the request never got an answer" looks
 * like, and `isPermanentRefusal(0)` is false, which is correct: a dead connection is the
 * most retryable failure there is.
 *
 * Both shapes are read because both occur: `TRPCClientError` exposes the server's payload as
 * `.data`, and a batched error that has been round-tripped through the persister keeps it
 * under `.shape.data`.
 */
export function refusalStatus(error: unknown): number {
  const e = error as
    | { data?: { httpStatus?: unknown }; shape?: { data?: { httpStatus?: unknown } } }
    | null
    | undefined;
  const raw = e?.data?.httpStatus ?? e?.shape?.data?.httpStatus;
  return typeof raw === 'number' ? raw : 0;
}

/** Server-confirmed planning refusal. Text, class names and a bare409 are not evidence. */
export function isStockBusy(error: unknown): boolean {
  const e = error as
    | {
        data?: { code?: unknown; httpStatus?: unknown; stockBusy?: unknown };
        shape?: { data?: { code?: unknown; httpStatus?: unknown; stockBusy?: unknown } };
      }
    | null
    | undefined;
  const data = e?.data ?? e?.shape?.data;
  return data?.httpStatus === 409 && data.code === 'CONFLICT' && data.stockBusy === true;
}

/** Mutation recovery only: the photo queue's status-only refusal policy stays unchanged. */
export function isRetryableMutationRefusal(error: unknown): boolean {
  return isStockBusy(error) || !isPermanentRefusal(refusalStatus(error));
}

/**
 * What the server said, in its own words.
 *
 * Kept verbatim rather than mapped to house copy, for the same reason the MCP layer quotes a
 * withheld field's reason instead of coding it: the consumer is a person who has to decide
 * what to do next, and a paraphrase is where an invented reason gets in. The fallback fires
 * only when there is genuinely nothing to quote.
 */
export function refusalReason(error: unknown): string {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  /**
   * ⚠️ Says nothing about the SERVER, on purpose. The first fallback read *"The server
   * didn't answer"* — which is a claim, and a false one for a failure that never reached a
   * server at all (a client-side throw, a serialisation error, an aborted request). The
   * panel prints this to a technician as fact, so it may only say what is true of every case
   * that lands here: something stopped, and nobody said why.
   */
  return 'Servwell couldn’t finish this, and didn’t say why.';
}
